import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FeatureRequestCard from "./FeatureRequestCard";
import { FeatureRequest } from "../../types/featureRequest";
import { createFeatureRequestBannerStyles } from "./styles";

/**
 * Props for the FeatureRequestList component
 */
interface FeatureRequestListProps {
  /** Array of feature requests to display */
  features: FeatureRequest[];
  /** Callback function to handle voting for features */
  onVote: (featureId: string, userId: string) => void;
  /** Function to check if the current user has voted for a feature */
  hasUserVoted: (featureId: string) => boolean;
  /** The current user's ID */
  userId: string;
  /** Whether to shuffle the order of features (default: true) */
  shuffle?: boolean;
}

/**
 * FeatureRequestList Component
 *
 * Displays a list of feature requests with voting functionality.
 * Features can be shuffled for variety and each card is expandable to show full descriptions.
 * Includes custom scrollbar styling and responsive layout.
 */
const FeatureRequestList: React.FC<FeatureRequestListProps> = ({
  features,
  onVote,
  hasUserVoted,
  userId,
  shuffle = true,
}) => {
  const theme = useTheme();
  const featureRequestBannerStyles = createFeatureRequestBannerStyles(theme);

  // Ensure features is always an array
  const safeFeatures = Array.isArray(features) ? features : [];

  let shuffledFeatures: FeatureRequest[];
  if (shuffle) {
    shuffledFeatures = [...safeFeatures].sort(() => Math.random() - 0.5);
  } else {
    shuffledFeatures = safeFeatures;
  }

  return (
    <Box sx={featureRequestBannerStyles.listContainer}>
      <Box sx={featureRequestBannerStyles.listWrapper}>
        <Typography variant="h6" sx={featureRequestBannerStyles.listTitle}>
          Vote for Features
        </Typography>
        <Box sx={featureRequestBannerStyles.listScrollContainer}>
          <Box sx={featureRequestBannerStyles.listItems}>
            {shuffledFeatures.map((feature) => (
              <FeatureRequestCard
                key={feature._id}
                feature={feature}
                onVote={onVote}
                hasUserVoted={hasUserVoted}
                userId={userId}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureRequestList;
